import React from "react";
import { Avatar, Button, Tooltip, Skeleton } from "antd";
import { Search, Users, Megaphone, User, PanelRight, ArrowLeft } from "lucide-react";
import { useSearchParamsV6 } from "../../../hooks/useSearchParamsV6";
import { useJoinAChatRoom } from "../../../hooks/queries/chat";
import styles from "./ChatHeader.module.scss";

interface ChatHeaderProps {
  onToggleRightPanel: () => void;
  onOpenSearch: () => void;
  onBack?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ onToggleRightPanel, onOpenSearch, onBack }) => {
  const [searchParams] = useSearchParamsV6();
  const roomId = searchParams.get("roomId");

  // Sử dụng hook useJoinAChatRoom để lấy thông tin phòng chat
  const { data: roomInfo, isLoading, isFetching, isError } = useJoinAChatRoom({ roomId: roomId || "" });

  const loading = isLoading || isFetching;

  // Xác định icon theo loại phòng
  const getRoomTypeIcon = (type?: string) => {
    switch (type) {
      case "C": // Channel/Kênh thông báo
        return <Megaphone size={14} className={styles.roomTypeIcon} />;
      case "U": // User/Cá nhân
        return <User size={14} className={styles.roomTypeIcon} />;
      case "D": // Department/Phòng ban
      case "P": // Public Group/Nhóm công khai
      case "G": // Created Group/Nhóm
      default:
        return <Users size={14} className={styles.roomTypeIcon} />;
    }
  };

  // Hiển thị loading state
  if (loading) {
    return (
      <div className={styles.chatHeader}>
        <div className={styles.leftSection}>
          {onBack && (
            <button className={styles.backButton} onClick={onBack}>
              <ArrowLeft size={20} />
            </button>
          )}
          <Skeleton.Avatar active size={48} />
          <div className={styles.chatInfo}>
            <Skeleton.Input active size="small" style={{ width: 150 }} />
            <Skeleton.Input active size="small" style={{ width: 100, marginTop: 4 }} />
          </div>
        </div>
        <div className={styles.rightSection}>
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
        </div>
      </div>
    );
  }

  // Hiển thị error state hoặc empty state
  if (isError || !roomInfo) {
    return (
      <div className={styles.chatHeader}>
        <div className={styles.leftSection}>
          {onBack && (
            <button className={styles.backButton} onClick={onBack}>
              <ArrowLeft size={20} />
            </button>
          )}
          <Avatar size={48} style={{ backgroundColor: "#ccc" }}>
            ?
          </Avatar>
          <div className={styles.chatInfo}>
            <h3 className={styles.chatName}>Chọn một cuộc trò chuyện</h3>
            <p className={styles.chatStatus}>
              <span className={styles.activity}>Không có dữ liệu</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatHeader}>
      <div className={styles.leftSection}>
        {onBack && (
          <button className={styles.backButton} onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
        )}
        <Avatar
          size={48}
          src={roomInfo.Data.AvatarUrl}
          style={{ backgroundColor: roomInfo.Data.AvatarUrl ? undefined : "#ff5c00" }}
        >
          {!roomInfo.Data.AvatarUrl && roomInfo.Data.RoomName?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div className={styles.chatInfo}>
          <h3 className={styles.chatName}>{roomInfo.Data.RoomName}</h3>
          <div className={styles.chatStatus}>
            <Users size={14} className={styles.userIcon} />
            <span className={styles.memberCount}>{roomInfo.Data.Members?.length || 0} thành viên</span>
            <span className={styles.roomTypeIconContainer}>{getRoomTypeIcon(roomInfo.Data.RoomType)}</span>
          </div>
        </div>
      </div>

      <div className={styles.rightSection}>
        <Tooltip title="Tìm kiếm">
          <Button type="text" icon={<Search size={18} />} onClick={onOpenSearch} className={styles.iconButton} />
        </Tooltip>

        <Tooltip title="Thông tin">
          <Button
            type="text"
            icon={<PanelRight size={18} />}
            onClick={onToggleRightPanel}
            className={styles.iconButton}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default React.memo(ChatHeader);
